'use client';

import { type FC, useEffect } from 'react';

import {
  buildGtagConsentFields,
  COOKIE_CONSENT_CHANGED_EVENT,
  type CookieConsentPreferences,
  getEffectiveCookieConsentPreferences,
  isCookieConsentPreferences,
  pushConsentToGtag,
} from './cookieConsentStorage';
import { getTrackingIds, HUBSPOT_SCRIPT_SRC } from './trackingIds';

let googleTagsInjected = false;
let hubspotInjected = false;

function appendInlineScript(id: string, code: string): void {
  if (document.getElementById(id)) {
    return;
  }

  const el = document.createElement('script');
  el.id = id;
  el.text = code;
  document.head.appendChild(el);
}

function appendExternalScript(src: string, id: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.getElementById(id)) {
      resolve();

      return;
    }

    const el = document.createElement('script');
    el.id = id;
    el.async = true;
    el.src = src;
    el.onload = () => resolve();
    el.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(el);
  });
}

function ensureGtagConsentDefault(prefs: CookieConsentPreferences): void {
  window.dataLayer = window.dataLayer || [];

  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer!.push(args);
    };
  }

  window.gtag('consent', 'default', {
    ...buildGtagConsentFields(prefs),
    wait_for_update: 500,
  });
}

async function injectGoogleTags(prefs: CookieConsentPreferences): Promise<void> {
  const { gtmId, gaMeasurementId } = getTrackingIds();

  ensureGtagConsentDefault(prefs);

  appendInlineScript(
    'googleTagManager',
    `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${gtmId}');`
  );

  await appendExternalScript(
    `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaMeasurementId)}`,
    'google-gtag-js'
  );

  appendInlineScript(
    'googleTagConfig',
    `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
window.gtag = gtag;
gtag('js', new Date());
gtag('config', '${gaMeasurementId}');`
  );
}

function injectHubSpot(): void {
  if (document.getElementById('hs-script-loader')) {
    return;
  }

  const el = document.createElement('script');
  el.id = 'hs-script-loader';
  el.async = true;
  el.src = HUBSPOT_SCRIPT_SRC;
  document.head.appendChild(el);
}

async function applyConsentPreferences(prefs: CookieConsentPreferences): Promise<void> {
  if (prefs.analytics && !googleTagsInjected) {
    googleTagsInjected = true;

    try {
      await injectGoogleTags(prefs);
    } catch {
      googleTagsInjected = false;
    }
  }

  if (prefs.marketing && !hubspotInjected) {
    hubspotInjected = true;
    injectHubSpot();
  }

  pushConsentToGtag(prefs);
}

export const ThirdPartyScripts: FC = () => {
  useEffect(() => {
    const onConsent = (event: Event): void => {
      const detail = (event as CustomEvent<CookieConsentPreferences>).detail;

      if (!isCookieConsentPreferences(detail)) {
        return;
      }

      void applyConsentPreferences(detail);
    };

    window.addEventListener(COOKIE_CONSENT_CHANGED_EVENT, onConsent);

    void applyConsentPreferences(getEffectiveCookieConsentPreferences());

    return () => window.removeEventListener(COOKIE_CONSENT_CHANGED_EVENT, onConsent);
  }, []);

  return null;
};
